// meeting-calendar.ts — Logique pure de la vue calendrier des réunions.
//
// Extraite de page-meetings-records pour être testable hors DOM (vitest).
// Aucune dépendance aux globals (t, esc, api…) ni au DOM : entrées =
// (année, mois) / id de projet, sorties = données pures.

export interface MonthCell {
  /** Date locale de la case (jour calendaire). */
  date: Date;
  /** Clé YYYY-MM-DD de la case. */
  key: string;
  /** Numéro du jour (1..31). */
  day: number;
  /** false pour les jours débordant avant le 1er / après le dernier du mois. */
  inMonth: boolean;
}

/** Clé YYYY-MM-DD à partir d'une Date locale (jour calendaire, sans fuseau). */
export function dateKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Couleur de fond déterministe par projet (aucun champ couleur en base) :
 *  l'angle d'or répartit les teintes pour des pastels distincts et stables.
 *  Pas de projet (null/0) → gris neutre. */
export function projectColor(projectId: number | null | undefined): { bg: string; fg: string } {
  if (!projectId) return { bg: '#e9edf3', fg: '#445069' };
  const hue = Math.round((projectId * 137.508) % 360);
  return { bg: `hsl(${hue}, 70%, 86%)`, fg: `hsl(${hue}, 50%, 26%)` };
}

/** Couleur de fond déterministe par type de comité (le « type » de comité
 *  EST le comité — COPIL, COPRO, … ; aucun champ couleur en base). Même
 *  principe d'angle d'or que projectColor, décalé de 60° pour des teintes
 *  perceptiblement distinctes des couleurs projet. Pas de comité → gris. */
export function committeeColor(committeeId: number | null | undefined): { bg: string; fg: string } {
  if (!committeeId) return { bg: '#e9edf3', fg: '#445069' };
  const hue = Math.round((committeeId * 137.508 + 60) % 360);
  return { bg: `hsl(${hue}, 70%, 86%)`, fg: `hsl(${hue}, 50%, 26%)` };
}

/** Grille mensuelle lundi→dimanche : de la semaine contenant le 1er à la
 *  semaine contenant le dernier jour du mois. La longueur est toujours un
 *  multiple de 7 (lignes pleines). `month` est 0-indexé (0 = janvier). */
export function buildMonthGrid(year: number, month: number): MonthCell[] {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startOffset = (first.getDay() + 6) % 7; // jours depuis lundi
  const endOffset = 6 - ((last.getDay() + 6) % 7); // jours jusqu'à dimanche
  const totalDays = startOffset + last.getDate() + endOffset;
  const cells: MonthCell[] = [];
  const cur = new Date(year, month, 1 - startOffset);
  for (let i = 0; i < totalDays; i++) {
    cells.push({
      date: new Date(cur),
      key: dateKey(cur),
      day: cur.getDate(),
      inMonth: cur.getMonth() === month,
    });
    cur.setDate(cur.getDate() + 1);
  }
  return cells;
}
